import { useContext } from 'react';

import {
  Button,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UseModalProps,
} from '@chakra-ui/react';
import { FormikContext, FormikContextType } from 'formik';
import { BiCopy } from 'react-icons/bi';

import { getFullName } from '../../gql/customerGql';
import { Deal, DealStateEnum, DealStateLabelMap } from '../../gql/dealGql';

import PayoffRequestButtons from '../GetPayoffButtons/PayoffRequestButtons';
import Modal from '../shared/Modal';

import ReviveBootButton from './ReviveBootButton';

import { PermissionEnum } from '../../constants/permissions';
import { useHasPermission } from '../../hooks/useHasPermission';
import { useUser } from '../../hooks/useUser';

type BaseProps = {
  duplicateDeals: Deal[];
  showPayoffRequestButton?: boolean;
  upsertEstimateLoading?: boolean;
  disableProceedButton?: boolean;
  proceedButtonLabel?: string;
  customMessage?: string;
  allowDuplicateCreation?: boolean;
} & UseModalProps;

type OnDealDetailProps = {
  proceedFunction: () => void;
  submitEstimate?: never;
};

type OnNewEstimateProps = {
  submitEstimate:
    | ((values: Deal, setSubmitting: (isSubmitting: boolean) => void) => void)
    | undefined;
  proceedFunction?: never;
};

// When allowDuplicateCreation is false, neither proceedFunction nor submitEstimate is needed
type NoProceedProps = {
  proceedFunction?: never;
  submitEstimate?: never;
};

type DuplicateDealsModalProps = BaseProps &
  (OnDealDetailProps | OnNewEstimateProps | NoProceedProps);

export const DuplicateDealsModal = ({
  duplicateDeals,
  isOpen,
  onClose,
  proceedFunction,
  submitEstimate,
  showPayoffRequestButton,
  proceedButtonLabel = 'CREATE DEAL',
  disableProceedButton = false,
  upsertEstimateLoading = false,
  customMessage,
  allowDuplicateCreation = true,
  ...rest
}: DuplicateDealsModalProps) => {
  const user = useUser();
  // Use useContext instead of useFormikContext to avoid throwing when no Formik provider exists
  const formikContext = useContext(FormikContext) as FormikContextType<Deal> | undefined;
  const values = formikContext?.values;
  const setSubmitting = formikContext?.setSubmitting;

  // if the user has permission to create duplicate deals or if all the duplicate deals were created by the user
  const canCreateDuplicateDeals =
    useHasPermission(PermissionEnum.CreateDuplicateDeals) ||
    duplicateDeals.every((d) => d.setter?.id === user.id);

  // order the duplicate deals
  const sortedDuplicateDeals = duplicateDeals.sort((a, b) => {
    return (
      new Date(b.creation_date_utc || '').getTime() - new Date(a.creation_date_utc || '').getTime()
    );
  });

  const showReviveBootButton = sortedDuplicateDeals.every((d) => d.state === DealStateEnum.Booted);

  const getRightHandButton = () => {
    if (showReviveBootButton) {
      return (
        <ReviveBootButton duplicateDeals={sortedDuplicateDeals} closeDuplicateModal={onClose} />
      );
    }
    if (!allowDuplicateCreation || !canCreateDuplicateDeals) {
      const handleCopyLink = () => {
        const links = duplicateDeals
          .map((d) => `${window.location.origin}/deals/${d.id}`)
          .join(',');
        navigator.clipboard.writeText(links);
        onClose();
      };

      return (
        <Button leftIcon={<BiCopy />} onClick={handleCopyLink}>
          Copy Link
        </Button>
      );
    }
    if (showPayoffRequestButton) {
      return <PayoffRequestButtons />;
    }

    const proceedAction =
      submitEstimate && values && setSubmitting
        ? () => submitEstimate(values, setSubmitting)
        : proceedFunction;

    return (
      <Button
        isLoading={upsertEstimateLoading}
        onClick={proceedAction}
        isDisabled={disableProceedButton}
      >
        {proceedButtonLabel}
      </Button>
    );
  };

  return (
    <Modal
      title="Duplicate Deal"
      isOpen={isOpen}
      onClose={onClose}
      leftButtons={
        <Button onClick={onClose} variant="warning">
          CANCEL
        </Button>
      }
      rightButtons={getRightHandButton()}
      size="4xl"
      {...rest}
    >
      <Text mb={8}>
        {customMessage ??
          'The information you have entered matches one or more deals already in the system. Please see below and send to your manager for review.'}
      </Text>
      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Vehicle</Th>
            <Th>Number</Th>
            <Th>Email</Th>
            <Th>State</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedDuplicateDeals.map((d) => (
            <Tr key={d.id}>
              <Td>
                <Link
                  href={`/deals/${d.id}`}
                  target="_blank"
                  rel="noreferrer"
                  width="10%"
                  fontWeight="bold"
                  color="mediumElectricBlue"
                >
                  {getFullName(d.customer)}
                </Link>
              </Td>
              <Td>
                {d.car.year} {d.car.make} {d.car.model}
              </Td>
              <Td whiteSpace="nowrap">{d.customer.phone_number}</Td>
              <Td whiteSpace="nowrap">{d.customer.email}</Td>
              <Td>{DealStateLabelMap[d.state]}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Modal>
  );
};
